import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Download, ArrowUpRight } from "lucide-react";
import { MaskLine, Reveal, ClipReveal } from "@/components/Reveal";
import { api, resolveImg } from "@/lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [one, list] = await Promise.all([
          api.get(`/products/${slug}`),
          api.get("/products"),
        ]);
        setProduct(one.data);
        setAllProducts(list.data || []);
      } catch {
        setProduct(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (product) document.title = `${product.title} — Manik Engineering`;
  }, [product]);

  if (loading) return <div className="min-h-screen bg-[#eaf6fc]" />;
  if (!product) return <Navigate to="/products" replace />;

  const nextIndex = (allProducts.findIndex((p) => p.slug === slug) + 1) % (allProducts.length || 1);
  const next = allProducts[nextIndex] || product;

  return (
    <div data-testid={`product-detail-${product.slug}`}>
      {/* HERO */}
      <section className="bg-[#eaf6fc] pt-40 pb-20 relative overflow-hidden border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
          <Reveal>
            <Link
              to="/products"
              data-testid="pd-back-to-products"
              className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#52525B] hover:text-[#3da8d8] mb-10"
            >
              <ArrowLeft size={13} /> All products
            </Link>
          </Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-6">
            {product.tagline}
          </p>
          <h1 className="font-display font-bold text-[#334155] text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-5xl">
            <MaskLine delay={0.1}>{product.title}</MaskLine>
          </h1>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              {product.pdf && (
                <a
                  href={resolveImg(product.pdf)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pd-catalogue-btn"
                  className="btn-fill group inline-flex items-center gap-3 border border-[#3da8d8] px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white"
                >
                  Download catalogue <Download size={14} className="group-hover:translate-y-0.5 transition-transform" />
                </a>
              )}
              <Link
                to={`/projects?category=${encodeURIComponent(
                  // ModulArt In-Plant Offices → ModulArt In-Plant Office (drop trailing "s" on the last word)
                  (product.title || "").replace(/s\s*$/i, "")
                )}`}
                data-testid="pd-see-projects-btn"
                className="group inline-flex items-center gap-3 border border-black/15 px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#334155] hover:border-[#3da8d8] hover:text-[#3da8d8] transition-colors"
              >
                See our projects <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/contact"
                data-testid="pd-enquire-btn"
                className="group inline-flex items-center gap-3 border border-black/15 px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#334155] hover:border-[#78be27] hover:text-[#78be27] transition-colors"
              >
                Enquire now <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* IMAGE + OVERVIEW */}
      {(product.image || product.overview || (product.features || []).length > 0) && (
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {product.image && (
            <ClipReveal className="lg:col-span-7 border border-black/10">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={resolveImg(product.image)} alt={product.title} className="w-full h-full object-cover" />
              </div>
            </ClipReveal>
          )}
          <div className={product.image ? "lg:col-span-5" : "lg:col-span-12"}>
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8]">Overview</p>
            </Reveal>
            {product.overview && (
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg text-[#334155] leading-relaxed">{product.overview}</p>
              </Reveal>
            )}
            {(product.features || []).length > 0 && (
              <Reveal delay={0.2}>
                <ul className="mt-8 grid gap-3">
                  {(product.features || []).map((f) => (
                    <li key={f} className="flex items-center gap-3 text-[#0A0D14]">
                      <span className="w-6 h-6 flex items-center justify-center bg-[#78be27]/15 shrink-0">
                        <Check size={14} className="text-[#78be27]" />
                      </span>
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
          </div>
        </div>
      </section>
      )}

      {/* FEATURES GROUPED */}
      {product.features_grouped?.length > 0 && (
      <section className="bg-white border-y border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">What sets it apart</p>
                <h2 className="font-display text-3xl lg:text-5xl tracking-tight text-[#334155]">Engineered for the demands of industry.</h2>
              </Reveal>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {(product.features_grouped || []).map((group, i) => (
              <Reveal key={group.title} delay={i * 0.08} className="bg-white">
                <div className="p-10 h-full">
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#78be27] mb-3">0{i + 1}</p>
                  <h3 className="font-display text-2xl text-[#334155] mb-6">{group.title}</h3>
                  <ul className="space-y-3">
                    {(group.items || []).map((it) => (
                      <li key={it} className="flex items-start gap-3 text-[#52525B] text-sm leading-relaxed">
                        <span className="w-1.5 h-1.5 mt-2 bg-[#3da8d8] shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* SPECS + APPLICATIONS */}
      {(product.specs?.length > 0 || product.applications?.length > 0 || product.gallery?.length > 0) && (
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">Specifications</p>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-[#334155] mb-10">Technical benchmarks.</h2>
            </Reveal>
            <div className="border-t border-black/10">
              {(product.specs || []).map((s, i) => (
                <Reveal key={s.label} delay={i * 0.04}>
                  <div className="flex justify-between items-baseline gap-6 py-4 border-b border-black/10">
                    <span className="font-mono-accent text-[11px] uppercase tracking-widest text-[#52525B]">{s.label}</span>
                    <span className="font-display text-base lg:text-lg text-[#334155] text-right">{s.value}</span>
                  </div>
                </Reveal>
              ))}
              {(!product.specs || product.specs.length === 0) && (
                <p className="py-4 text-sm text-[#94A3B8] italic">Specifications coming soon.</p>
              )}
            </div>
          </div>
          <div>
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">Applications</p>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-[#334155] mb-10">Where it works.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-3">
                {(product.applications || []).map((a) => (
                  <span
                    key={a}
                    className="inline-flex items-center border border-black/15 bg-white px-4 py-2 font-mono-accent text-[11px] uppercase tracking-widest text-[#334155]"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </Reveal>

            {product.gallery?.length > 0 && (
              <div className="mt-12 grid grid-cols-3 gap-3">
                {product.gallery.map((g, i) => (
                  <ClipReveal key={i} delay={i * 0.08} className="aspect-square border border-black/10 overflow-hidden">
                    <img src={resolveImg(g)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[900ms]" />
                  </ClipReveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* CTA + NEXT */}
      <section className="bg-[#0e5f84] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#78be27] mb-4">Ready to spec your project?</p>
            <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight leading-tight">
              Let&apos;s build the right {product.title.toLowerCase()} for your site.
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-wrap items-center gap-4 lg:justify-end">
            <Link
              to="/contact"
              data-testid="pd-cta-contact"
              className="inline-flex items-center gap-3 border border-[#78be27] bg-[#78be27] text-white px-7 py-4 font-mono-accent text-xs uppercase tracking-widest hover:bg-transparent transition-colors"
            >
              Talk to us <ArrowRight size={15} />
            </Link>
            <Link
              to={`/products/${next.slug}`}
              data-testid="pd-cta-next"
              className="inline-flex items-center gap-3 border border-white/40 text-white px-7 py-4 font-mono-accent text-xs uppercase tracking-widest hover:bg-white/10 transition-colors"
            >
              Next: {next.title.split(" ").slice(0, 3).join(" ")} <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
